import { DragDropContext, Draggable, DraggableProvidedDraggableProps, Droppable } from '@hello-pangea/dnd';
import { Card, Group, rem, Stack, Text, Title } from '@mantine/core';
import { IconGripVertical, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import cx from 'clsx';
import { useParams } from 'react-router-dom';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    defaultContentSectionResource,
    useContentSectionActions,
} from '../../../resource/resources/artifacts/content/contentSectionResource';
import { TOOLTIPS } from '../../../resource/tooltipTexts';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { DisableableIconButtonWithTooltip } from '../../reusable-components/DisableableIconButtonWithTooltip';
import { ResourceValidationTitleBar } from '../../reusable-components/validation/ResourceValidationTitleBar';
import classes from './../../util/DndListHandle.module.css';
import { ContentSectionDisplay } from './ContentSectionDisplay';
import { ContentSectionEditDialog } from './ContentSectionEditDialog';

export const ContentSectionContent = ({ contentId, readOnly }: { contentId: string; readOnly: boolean }) => {
    const { versionId } = useParams<{ versionId: string | undefined }>();
    const nestedDialog = useNestedDialogFunction();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const contentSectionActions = useContentSectionActions();

    const contentSectionsQuery = contentSectionActions.useGetAllResources(contentId);

    const addContentSection = contentSectionActions.useAddResourceToIndex();
    const reorderContentSection = contentSectionActions.useChangeIndexOfResource();
    const deleteContentSection = contentSectionActions.useDeleteResource();

    const onAdd = () => {
        addContentSection
            .mutateAsync({
                resource: {
                    ...defaultContentSectionResource,
                },
                index: null,
                parentId: contentId,
            })
            .then(() => {
                setTimeout(() => {
                    const drawerRef = document.getElementsByClassName('mantine-Drawer-content').item(0);
                    drawerRef?.scrollTo({ top: 9999, behavior: 'smooth' });
                }, 400);
            });
    };

    const onDelete = (id: string) => {
        deleteResourceDialogFunction.deleteResource(id, null, ResourceType.CONTENT_SECTION, deleteContentSection);
    };

    const onEdit = (id: string) => {
        nestedDialog.showNestedDialog(
            <ContentSectionEditDialog
                onClose={nestedDialog.closeNestedDialog}
                contentSectionId={id}
                versionId={versionId ?? ''}
            />
        );
    };

    const getTopPositionOfDraggable = (draggableStyle: DraggableProvidedDraggableProps) => {
        const scrollTop = document.getElementsByClassName('mantine-Drawer-content').item(0)?.scrollTop ?? 0;
        if (draggableStyle.style && 'top' in draggableStyle.style) {
            return draggableStyle.style?.top + scrollTop;
        }
        return undefined;
    };

    const contentSectionCards = contentSectionsQuery.data?.map((section) => (
        <div key={section.id} style={{ width: '100%' }}>
            <Card withBorder radius={'md'} w={'100%'}>
                {Boolean(section.title) && <Title order={4}>{section.title}</Title>}
                <Card.Section inheritPadding py="xs">
                    <ContentSectionDisplay contentSectionId={section.id} />
                </Card.Section>
            </Card>
        </div>
    ));

    const draggableContentSectionCards = contentSectionsQuery.data?.map((section, index) => (
        <Draggable draggableId={section.id} index={index} key={section.id}>
            {(provided, snapshot) => (
                <Card
                    withBorder
                    radius={'md'}
                    w={'100%'}
                    className={cx({ [classes.itemDragging]: snapshot.isDragging })}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    style={{
                        ...provided.draggableProps.style,
                        top: getTopPositionOfDraggable(provided.draggableProps),
                    }}
                >
                    {Boolean(section.title) && <Title order={4}>{section.title}</Title>}
                    <Card.Section inheritPadding py="xs">
                        <div style={{ display: 'inline-flex', width: '100%', alignItems: 'center' }}>
                            <div {...provided.dragHandleProps} className={classes.dragHandle}>
                                <IconGripVertical style={{ width: rem(18), height: rem(18) }} stroke={1.5} />
                            </div>
                            <Stack align={'flex-start'} justify={'flex-start'} w={'100%'}>
                                <Group w={'100%'} wrap={'nowrap'} style={{ alignItems: 'start' }}>
                                    <div style={{ width: '93%' }}>
                                        <ContentSectionDisplay contentSectionId={section.id} />
                                    </div>
                                    <div style={{ flexGrow: 1 }} />
                                    <Group justify={'flex-end'}>
                                        <DisableableIconButtonWithTooltip
                                            disabled={false}
                                            tooltipText={TOOLTIPS.EDIT}
                                            icon={<IconPencil />}
                                            onClick={() => onEdit(section.id)}
                                        />
                                        <DisableableIconButtonWithTooltip
                                            disabled={false}
                                            tooltipText={TOOLTIPS.DELETE}
                                            icon={<IconTrash />}
                                            onClick={() => onDelete(section.id)}
                                        />
                                    </Group>
                                </Group>
                            </Stack>
                        </div>
                    </Card.Section>
                </Card>
            )}
        </Draggable>
    ));

    const dragAndDrop = () => {
        return (
            <DragDropContext
                onDragEnd={({ destination, source }) => {
                    const resource = contentSectionsQuery.data?.at(source.index);
                    const destinationIndex = destination?.index;
                    if (resource && destinationIndex !== undefined) {
                        reorderContentSection.mutateAsync({
                            resource,
                            index: destinationIndex,
                            parentId: contentId,
                        });
                    }
                }}
            >
                <Droppable droppableId={'dnd-list'} direction={'vertical'}>
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} style={{ width: '100%' }}>
                            <Stack gap={5}>
                                {draggableContentSectionCards}
                                {provided.placeholder}
                            </Stack>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    };

    return (
        <Stack align="flex-start" justify="flex-start" w={'100%'} gap={5}>
            <Show when={!readOnly}>
                <ResourceValidationTitleBar
                    customButtons={
                        <DisableableIconButtonWithTooltip
                            disabled={false}
                            tooltipText={TOOLTIPS.CREATE_NEW_REFERENCE}
                            icon={<IconPlus />}
                            onClick={onAdd}
                        />
                    }
                >
                    <Text c={'dimmed'}>Content Sections</Text>
                </ResourceValidationTitleBar>
                {dragAndDrop()}
            </Show>
            <Show when={readOnly}>{contentSectionCards}</Show>
        </Stack>
    );
};
